import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { generateClient } from 'aws-amplify/api';
import { Building2, Plus, Search, ChevronLeft, ChevronRight, RefreshCw, Users } from 'lucide-react';
import { Tenant, TenantConnection } from '../../types/admin';
import { LIST_TENANTS } from '../../graphql/admin';
import { useAuth } from '../../contexts/AuthContext';
import TenantStatusBadge from '../../components/admin/TenantStatusBadge';
import CreateTenantModal from '../../components/admin/CreateTenantModal';
import { CREATE_TENANT } from '../../graphql/admin';

/**
 * TenantListPage - Display all tenants with search, filter, and pagination
 * Requirements: 10.1, 12.3
 */
const TenantListPage: React.FC = () => {
  const { loading: authLoading, error: authError } = useAuth();
  const navigate = useNavigate();
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [nextToken, setNextToken] = useState<string | null>(null);
  const [prevTokens, setPrevTokens] = useState<string[]>([]);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const PAGE_SIZE = 10;

  // Handle authorization errors - redirect to unauthorized page
  useEffect(() => {
    if (!authLoading && authError) {
      // Check if it's an authorization error
      if (authError.toLowerCase().includes('unauthorized') || 
          authError.toLowerCase().includes('forbidden') ||
          authError.toLowerCase().includes('not found')) {
        navigate('/unauthorized', { replace: true });
      }
    }
  }, [authLoading, authError, navigate]);

  const fetchTenants = useCallback(async (token?: string | null) => {
    try {
      setLoading(true);
      setError(null);

      // Check if we're in preview mode
      const isPreviewMode = import.meta.env.VITE_PREVIEW === 'true' || 
                           !import.meta.env.VITE_COGNITO_USER_POOL_ID;

      if (isPreviewMode) {
        // Mock data for preview mode
        setTenants([
          { tenantId: '1', name: 'Acme Corp', status: 'ACTIVE' as any, userCount: 15, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
          { tenantId: '2', name: 'Beta Inc', status: 'ACTIVE' as any, userCount: 8, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
          { tenantId: '3', name: 'Gamma LLC', status: 'INACTIVE' as any, userCount: 3, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
        ]);
        setNextToken(null);
        return;
      }

      const client = generateClient();
      const response = await client.graphql({
        query: LIST_TENANTS,
        variables: { limit: PAGE_SIZE, nextToken: token }
      }) as any;

      const data: TenantConnection = response.data?.listTenants;
      if (data) {
        setTenants(data.items);
        setNextToken(data.nextToken);
      }
    } catch (err: any) {
      console.error('Failed to fetch tenants:', err);
      setError(err.message || 'Failed to load tenants');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTenants();
  }, [fetchTenants]);

  const handleNextPage = () => {
    if (nextToken) {
      setPrevTokens([...prevTokens, '']); // Store current position
      fetchTenants(nextToken);
    }
  };

  const handlePrevPage = () => {
    if (prevTokens.length > 0) {
      const newPrevTokens = [...prevTokens];
      const prevToken = newPrevTokens.pop();
      setPrevTokens(newPrevTokens);
      fetchTenants(prevToken || null);
    }
  };

  const handleCreateTenant = async (name: string) => {
    const isPreviewMode = import.meta.env.VITE_PREVIEW === 'true' || 
                         !import.meta.env.VITE_COGNITO_USER_POOL_ID;

    if (isPreviewMode) {
      // Mock creation for preview
      const newTenant: Tenant = {
        tenantId: Date.now().toString(),
        name,
        status: 'ACTIVE' as any,
        userCount: 0,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      setTenants([newTenant, ...tenants]);
      return;
    }

    const client = generateClient();
    await client.graphql({
      query: CREATE_TENANT,
      variables: { input: { name } }
    });
    
    // Refresh the list
    fetchTenants();
  };

  // Filter tenants by search query
  const filteredTenants = tenants.filter(tenant =>
    tenant.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Tenant Management</h1>
          <p className="mt-1 text-sm text-gray-600">
            Manage all tenants on the platform
          </p>
        </div>
        <button
          onClick={() => setIsCreateModalOpen(true)}
          className="inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 transition-all shadow-lg"
        >
          <Plus className="w-4 h-4 mr-2" />
          Create Tenant
        </button>
      </div>

      {/* Search and filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search tenants..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <button
          onClick={() => fetchTenants()}
          className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </button>
      </div>

      {/* Error state */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          {error}
        </div>
      )}

      {/* Loading state */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        </div>
      ) : (
        <>
          {/* Tenant table */}
          <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tenant
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredTenants.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-6 py-12 text-center text-gray-500">
                      <Building2 className="mx-auto h-12 w-12 text-gray-300 mb-4" />
                      <p>No tenants found</p>
                    </td>
                  </tr>
                ) : (
                  filteredTenants.map((tenant) => (
                    <tr key={tenant.tenantId} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                            <Building2 className="h-5 w-5 text-blue-600" />
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {tenant.name}
                            </div>
                            <div className="text-sm text-gray-500">
                              ID: {tenant.tenantId.slice(0, 8)}...
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <TenantStatusBadge status={tenant.status} />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(tenant.createdAt).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <Link
                          to={`/platform-admin/tenants/${tenant.tenantId}`}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          View Details
                        </Link>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-600">
              Showing {filteredTenants.length} tenant{filteredTenants.length !== 1 ? 's' : ''}
            </p>
            <div className="flex items-center space-x-2">
              <button
                onClick={handlePrevPage}
                disabled={prevTokens.length === 0}
                className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Previous
              </button>
              <button
                onClick={handleNextPage}
                disabled={!nextToken}
                className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Next
                <ChevronRight className="w-4 h-4 ml-1" />
              </button>
            </div>
          </div>
        </>
      )}

      {/* Create Tenant Modal */}
      <CreateTenantModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSubmit={handleCreateTenant}
      />
    </div>
  );
};

export default TenantListPage;
